import { getBookingPrecision } from '@clubmed/domains-service';
import { constants } from '@clubmed/utils';
import {
  activityCategoryFragment,
  activityFragment,
  rentalsFragment,
} from 'Client/cms/toolkit/product-utils/productFeatures.queries';
import {
  childcaresQuery,
  insuranceQuery,
  servicesWithSchedulePricingInfoQuery,
  transfersQuery,
  whiteLabelServicesQuery,
} from 'Components/utils/booking/booking-additional-services/BookingAddtionalServices.utils';
import graphqlClient from 'Utils/graphql-client/graphqlClient';
import { PACKAGE_ALL_INCLUSIVE } from 'Utils/proposal/constants';
import { CUSTOMER_ACCOUNT_GET_BOOKING_DATA } from 'Utils/request/requestDictionary';
const { JOYVIEW_ALL_INCLUSIVE, JOYVIEW_FOOD_AND_BEVERAGES, JOYVIEW_ROOM_ONLY } = constants.PACKAGES;
const CHILDREN_ACTIVITY_IDENTIFIER = '_CHILD_';
type BookingAttendeesParams = {
  bookingId: string;
  isJoyview: boolean;
  productId: string;
  withPackage?: boolean;
};
export type CustomerBookingsQueryParams = {
  isCanceled?: boolean;
  limit: number;
  page?: number;
  period: BookingsPeriods;
};
export type CustomerBookingsQueryOptions = {
  withUpsells?: boolean;
};
export const getPrePaymentQuery = ({
  bookingId,
  customerId,
  isCartPayment,
  isUpgradeRoomPayment,
}: {
  bookingId: string;
  customerId: string;
  isUpgradeRoomPayment: boolean;
  isCartPayment: boolean;
}) => `query {
      config {
        booking {
          banking {
            freeDepositDeadline
          }
          sellers {
            banking {
              freeDepositDeadline
            }
          }
        }
      }
      bookingsCart(bookingIds : ["${bookingId}"]) @include(if: ${String(isCartPayment)}) {
        cartBookings {
          count
          hasServicesInOption
          price {
            total {
              value
              currency
            }
          }
        }
      }
      bookingAccomodationsCart(bookingId: "${bookingId}", customerId: "${customerId}") @include(if: ${String(isUpgradeRoomPayment)}) {
        cart {
          count
          price {
            total {
              value
              currency
            }
          }
        }

      }
      userBooking(bookingId : "${bookingId}") {
        information {
          shouldProposeOnlinePayment
          status
          stays {
            id
            departureDate
          }
        }
        paymentDetails {
          duePayment {
            deadline
            amount {
              value
            }
          }
        }
      }
  }`;
export const getBookingTransportQuery = (bookingId: string) => `query {
      userBooking(bookingId: "${bookingId}") {
        transports {
          inbounds {
            ...transportsDetails
          }
          multiTransport
          outbounds {
            ...transportsDetails
          }
        }
      }
    }
    fragment transportsDetails on TransportDetailType {
      stayId
      transferIncluded
      travelSections {
        arrival {
          airportLabel
          cityId
          date
          id
          label
          terminalCode
          time
        }
        connectionDurationInMin
        departure {
          airportLabel
          cityId
          date
          id
          label
          terminalCode
          time
        }
        durationInMin
        isClubmedTransport
        stopDurationInMin
        technicalStops
        transport {
          baggage {
            quantity
            weight
            unit
          }
          class
          companyExploiter {
            id
            label
            logo
          }
          companyOperator {
            id
            label
            logo
            url
          }
          reference
          travelNumber
          type
          carbonEmission
          averageCarbonEmission
        }
      }
    }`;
// FRAGMENTS
export const packagesFragment = `
    packages(packageIds : ["${JOYVIEW_ROOM_ONLY}", "${JOYVIEW_ALL_INCLUSIVE}", "${JOYVIEW_FOOD_AND_BEVERAGES}"]) {
      ... on ProductPackageType {
        additionalInfo
        description
        id
        image
        label
        prices {
          perAdultPerNight {
            currency
            formattedValue
            value
          }
          perKidPerNight {
            currency
            formattedValue
            value
          }
        }
      }
    }`;
export const priceDetailsFragment = (appContext: AppContext) => `
    fragment priceDetails on PriceDetailType {
        formattedValue(precision: ${getBookingPrecision(appContext.locale)})
        value
    }`;
export const getStayAccommodationFragment = () => `
  fragment accommodations on AccommodationType {
    attendeesDistribution {
      attendees {
        birthdate
        customerId
        id
        type
      }
      id
      quantity
    }
    bathroomEquipments {
      id
      label
    }
    category
    categoryId
    comfortId
    comfortType
    description
    endDate
    equipments {
      id
      label
    }
    exclusiveCollection
    guest
    id
    label
    maxCapacity
    media {
      photos {
        url
      }
    }
    outsideFacilities {
      id
      label
    }
    quantity
    ranking
    rooms {
      number
    }
    roomType
    services {
      id
      label
    }
    startDate
    strengths {
      id
      label
    }
    surface
    view {
      id
      label
    }
    floorPlans
  }`;

const getStayUpsellsQuery = (appContext) => `
upsells(types: [CHILDCARE, EXCURSION, INSURANCE, LAND_SPORT, RENTAL, TRANSFER, WATER_SPORT, WINTER_SPORT, WELLNESS, PARKING, CONCIERGERIE]) {
  activities {
    ${servicesWithSchedulePricingInfoQuery}
    productInfo {
      id
      isIncluded
      label
      parentActivityCategoriesId
    }
  }
  rentals {
    ${servicesWithSchedulePricingInfoQuery}
    productInfo {
      id
      label
      ageInMonths {
        min
        max
      }
      useCase {
        id
        label
      }
      typeId: type
      typeLabel
      description: longDescription
      sizes {
        min
        max
        unit
      }
      practiceLevels {
        id
        label
      }
    }
  }
  ${childcaresQuery}
  ${insuranceQuery(appContext)}
  ${packagesFragment}
  ${transfersQuery(appContext)}
  ${whiteLabelServicesQuery(appContext)}
}
`;

const getStayTeaserBookingWithoutStaysFragment = () => {
  return `fragment stayBooking on CustomerBookingWithoutStaysType {
    attendees {
      birthday
      civility
      customerId
      email
      firstName:firstname
      id
      lastName:lastname
      mainContact
      type
      unicodeFirstname
      unicodeLastname
    }
    creationDate
    dates {
      duration
      endDate
      startDate
    }
    categories {
      isOption
      isOptionRenewed
      isExpiredOption
      isFromRenewal
      isStandard
      isTravelPlan
      isTravelPlanPrebooking
      isTravelPlanStandard
    }
    id
    isMultiSaleContracts
    isMultiStays
    optionDurability {
      expirationDateTime
      isReliable
    }
    payment {
      due {
        deadlineDate
        formattedValue
        value
      }
      isEnabled
      status {
        isPaid
        isPending
        isUnPaid
      }
    }
    status {
      name
      isArchived
      isExpiredOption
      isCanceled
      isValidated
    }
    totalPrice {
      value
    }
    vendor {
      isSoldByClubmed
      isSoldByPartners
      name
      phoneNumber
      type
    }
  }`;
};

const getStayTeaserBookingStayProductFragment = () => {
  return `fragment stayProduct on Resort {
    activityTree {
      ...activityCategory
      activities {
        ...activity
      }
      categories {
        ...activityCategory
        activities {
          ...activity
        }
        categories {
          ...activityCategory
          activities {
            ...activity
          }
        }
      }
    }
    associatedResort {
      id
      productTypeId
    }
    category
    childrenActivities: productActivities(idFilter: "${CHILDREN_ACTIVITY_IDENTIFIER}") {
      ageInMonths {
        max
        min
      }
      description
      equipments {
        id
        label
      }
      id
      isIncluded
      label
      subtitle
    }
    comfort
    destination {
      country
    }
    esfResortId
    facilities(types: ["CHILDREN"], withEquipments: true) {
      equipments {
        id
        label
      }
      label
      subtitle
    }
    href
    id
    images
    isCruise
    isExclusiveCollection
    isExplicitEC
    isJoyview
    isTour
    name
    rangeLabel
    rentalsTree {
      ...rentals
    }
    seasonTrad
    type
  }
  ${activityFragment}
  ${activityCategoryFragment}
  ${rentalsFragment}`;
};

export const getStayTeaserBookingFragment = (
  appContext: AppContext,
  options: CustomerBookingsQueryOptions,
) => `
  fragment booking on CustomerBookingType {
    categories {
      isOption
      isOptionRenewed
      isExpiredOption
      isFromRenewal
      isStandard
      isTravelPlan
    }
    dates {
      duration
      endDate
      startDate
    }
    id
    optionDurability {
      expirationDateTime
      isReliable
    }
    status {
      name
    }
    stays {
      ${options.withUpsells ? 'accommodations {  ...accommodations }' : ''}
      accommodationsCount
      booking {
        ...stayBooking
      }
      dates {
        duration
        endDate
        isCurrent
        isPast
        isToCome
        startDate
      }
      features {
        easyArrival {
          status {
            isAvailable
            isDone
          }
          url
        }
        easyCheckInOut {
          easyCheckIn {
            isActivated
            status {
              isAvailable
              isUneditable
            }
          }
          easyCheckOut {
            isActivated
            status {
              isAvailable
              isUneditable
            }
          }
        }
        easyTransfer {
          serviceStatus {
            isUpsellAvailable
            isValidated
          }
        }
        policyForm {
          status {
            isAvailable
          }
        }
        feedback {
          status {
            isAvailable
          }
          url
        }
        pickYourRoom {
          status {
            name
          }
        }
      }
      id
      participants {
        attendees {
          id
        }
        adultsCount
        children {
          birthday
        }
        childrenCount
      }
      product {
        ...stayProduct
      }
      stayIndex
      transfers {
        id
      }
      transport {
        fromCity
        fromCityCode
        inbounds {
          stayId
        }
        outbounds {
          stayId
        }
        type
        types
      }
      ${options.withUpsells ? getStayUpsellsQuery(appContext) : ''}
      vouchers {
        value
        unit
        currency
        dateOfUse
        isAvailable
        isFTC
        validityEndDate
      }
    }
  }
  ${options.withUpsells ? [priceDetailsFragment(appContext), getStayAccommodationFragment()].join('') : ''}
  ${getStayTeaserBookingWithoutStaysFragment()}
  ${getStayTeaserBookingStayProductFragment()}
`;
// QUERIES
const profileQuery = `
  profile {
    civility
    firstName
    GMNumber
    isProspect
    lastName
    loyaltyProgram {
      platinumForLifeSince
      isPlatinumForLife
      points
      status
    }
    unicodeCivility
    unicodeFirstName
    unicodeLastName
  }`;
export const getCustomerProfileQuery = ({
  isQuickLogged = false,
}: {
  isQuickLogged: boolean;
}) => `query {
  loyaltyProgram {
    status
    threshold
  }
    ${isQuickLogged ? '' : profileQuery}
}`;
export const getCutomersQuery = () => `query {
  customers {
    currentTripsCount
    futureTripsCount
    hasSponsorship
    pastTripsCount
    searchesCount
    type
  }
}`;
export const getCustomerBookingAttendeesQuery = ({
  bookingId,
  isJoyview,
  productId,
  withPackage = false,
}: BookingAttendeesParams) => {
  const packageQuery = withPackage
    ? `
        package(productId : "${productId}", packageId : "${PACKAGE_ALL_INCLUSIVE}") {
            description
            id
            label
            prices {
              perAdultPerNight {
                formattedValue
              }
              perKidPerNight {
                formattedValue
              }
            }
          }
        `
    : '';
  const packageFragment = isJoyview
    ? `
          package {
            id
            isAllInclusive
            isRoomOnly
            label
          }
        `
    : '';
  const packagesUpsellQuery = withPackage
    ? `
            packagesUpsell {
              amount
              currency
              id
            }
          `
    : '';
  return `query {
          customerBooking(id: "${bookingId}") {
            attendees {
              birthday
              civility
              customerId
              email
              firstname
              id
              lastname
              mainContact
              type
              unicodeFirstname
              unicodeLastname
              ${packageFragment}
              ${packagesUpsellQuery}
            }
          }
          ${packageQuery}
        }`;
};
export const getCustomerBookingDocumentsQuery = (bookingId: string) => `
  query {
    customerBooking(id: "${bookingId}") {
      documents {
        clicType
        id
        label
        type
      }
    }
  }`;
export const getCustomerBookingPriceQuery = (appContext: AppContext, bookingId: string) => {
  const precision = getBookingPrecision(appContext.locale);
  return `query {
    customerBooking(id: "${bookingId}") {
        totalPrice {
          formattedValue(precision: ${precision})
          value
        }
      }
    }`;
};
export const getCustomerBookingPriceAndPaymentQuery = (
  appContext: AppContext,
  bookingId: string,
) => {
  const precision = getBookingPrecision(appContext.locale);
  return `query {
    customerBooking(id: "${bookingId}") {
      payment {
        due {
          deadlineDate
          formattedValue
          schedules {
            deadlineDate
            formattedValue
            value
          }
          value
        }
        paid {
          formattedValue
          value
        }
        status {
          isPending
        }
      }
      totalPrice {
        details {
          cancellationInsurance {
            formattedValue(precision: ${precision})
            value
          }
          discount {
            formattedValue(precision: ${precision})
            value
          }
          donation {
            formattedValue(precision: ${precision})
            value
          }
          fees {
            formattedValue(precision: ${precision})
            value
          }
          onDemand {
            formattedValue(precision: ${precision})
            id
            label
            value
          }
          package {
            formattedValue(precision: ${precision})
            value
          }
          total {
            formattedValue(precision: ${precision})
            value
          }
        }
      }
      vendor {
        isSoldByClubmed
      }
    }
  }`;
};

const getRenewedOptionByIdQuery = (bookingId: string) => {
  return `query {
    customerBooking(id: "${bookingId}") {
      optionDurability {
        expirationDateTime
      }
    }
  }`;
};

export const getRenewedOptionById = (appContext: AppContext, bookingId: string) => {
  return graphqlClient.runNoCacheQuery(getRenewedOptionByIdQuery(bookingId), appContext, {
    requestName: CUSTOMER_ACCOUNT_GET_BOOKING_DATA,
    strictErrors: false,
  });
};
export const getVouchersQuery = ({
  isSponsorship = false,
}: {
  isSponsorship?: boolean;
} = {}) => `query {
    vouchers {
      items {
        advantageCode
        currency
        dateOfUse
        godchildInformation {
          firstName
          lastName
          unicodeFirstName
          unicodeLastName
        }
        godfatherInformation {
          firstName
          lastName
          unicodeFirstName
          unicodeLastName
        }
        id
        isAvailable
        isExpired
        isFTC
        isTargetedOffer
        ${
          !isSponsorship
            ? `offer {
          description
          id
          name
          shortDescription}`
            : ''
        }

        origin {
          bookingId
          stay {
            endDate
            product {
              destination {
                country
              }
              href
              name
            }
            startDate
          }
        }
        originType
        status
        type
        validityEndDate
        value
        unit
      }
      type
    }
 }`;
export const DEFAULT_PAGINATED_TRIPS_LIST_LIMIT = 5;
export const DEFAULT_NOT_PAGINATED_TRIPS_LIST_LIMIT = 10;
export const DEFAULT_TRIPS_LIST_PAGE = 1;
export const getCustomerBookingsQuery = (
  appContext: AppContext,
  params: CustomerBookingsQueryParams,
  options: CustomerBookingsQueryOptions,
) => {
  const { isCanceled = false, limit, page = DEFAULT_TRIPS_LIST_PAGE, period } = params;
  const periods =
    period === 'all'
      ? '"CURRENTS" "FUTURS" "PASTS"'
      : '"' +
        {
          past: 'PASTS',
          future: 'FUTURS',
          current: 'CURRENTS',
        }[period] +
        '"';
  const statuses = isCanceled ? '"CANCELED"' : '"VALIDATED"';
  const limits = !isCanceled ? 'limit:' + limit + ' page:' + page : '';
  return `query {
    customerBookings(period: [${periods}], status: [${statuses}], ${limits}) {
        ...BookingsFragment
    }
  }
  ${getStayTeaserBookingFragment(appContext, options)}
  fragment BookingsFragment on PaginatedCustomerBookings {
    data {
      ...booking
    }
    pageInfo {
      currentPage
      hasNextPage
    }
  }
  `;
};
